import React, { FC } from 'react';
import { DetailsSection } from '@app/screens/nft-marketplace/MakeOfferDialog/components/DetailsSection';
import { formatUnits } from 'ethers/lib/utils';
import { VStack } from '@chakra-ui/react';
import { AmountInput } from '@app/screens/nft-marketplace/MakeOfferDialog/components/AmountInput';
import { SubmitSection } from '@app/screens/nft-marketplace/MakeOfferDialog/components/SubmitSection';
import { NFTMarketplaceListing } from '@app/types/nftMarketplace';
import { useGetBestBidForListing } from '@app/hooks/nft/useGetBestBidForListing';
import { useMakeAnOffer } from '@app/hooks/nft/useMakeAnOffer';
import { FormProvider, useForm } from 'react-hook-form';
import { showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import { Token } from '@app/types/token';
import { getAuctionDescription } from '@app/helpers/nft/getAuctionDescription';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAddress } from '@thirdweb-dev/react';
import { useTokenBalance } from '@app/hooks/token/useTokenBalance';
import { formatNumber, parseUnits } from '@app/helpers/format';
import { FormControl } from '@app/components/FormControl';
import { useGetCurrentPriceForDutchAuction } from '@app/hooks/nft/useGetCurrentPriceForDutchAuction';
import { dutchAuctionSchema } from '@app/screens/nft-marketplace/MakeOfferDialog/components/DutchAuction/helpers';
import { useKpNftPositionDetails } from '@app/screens/positions/components/SpNftPositionListRowItem/hooks';
import { useDiscountValue } from '@app/hooks/nft/useDiscountValue';
import { useNftMarketplaceFiltersState } from '@app/state/nftMarketplaceFiltersStore';

interface Props {
  listing: NFTMarketplaceListing;
  sellCurrency: Token;
  onClose: () => void;

  tokenId: string;
  lpTokenId: string;
  poolAddress: string;
}

type Form = {
  amount: string;
  token: Token;
};

export const DutchAuction: FC<Props> = ({
  listing,
  sellCurrency,
  onClose,
  tokenId,
  lpTokenId,
  poolAddress
}) => {
  const address = useAddress();
  const { data: bestBidForListing } = useGetBestBidForListing(listing.id);
  const { data: currentPrice } = useGetCurrentPriceForDutchAuction(listing.id);
  const { balance } = useTokenBalance(
    sellCurrency.contractAddress,
    address ?? ''
  );
  const { ownedPremiumNFTTokenId } = useNftMarketplaceFiltersState();

  const { usdAmount, data } = useKpNftPositionDetails(
    tokenId,
    poolAddress,
    lpTokenId
  );

  const discount = useDiscountValue(listing, sellCurrency, usdAmount);

  const { makeAnOffer, isLoading } = useMakeAnOffer();
  const methods = useForm<Form>({
    defaultValues: {
      amount: '',
      token: sellCurrency
    },
    resolver: yupResolver(dutchAuctionSchema),
    context: {
      reservePrice: formatUnits(listing.reserve, sellCurrency.decimals),
      bestBidForListing: bestBidForListing
        ? formatUnits(bestBidForListing.amount, sellCurrency.decimals)
        : 0,
      balance,
      currentPrice: currentPrice
        ? formatUnits(currentPrice, sellCurrency.decimals)
        : 0
    }
  });
  const {
    watch,
    setValue,
    handleSubmit,
    formState: { errors }
  } = methods;

  const amount = watch('amount');
  const token = watch('token');

  const onSubmit = async (values: Form) => {
    const txHash = await makeAnOffer({
      listingId: listing.id,
      amount: parseUnits(values.amount, sellCurrency.decimals),
      optionalPremiumTokenId: listing.nftMetadata.isPremium
        ? ownedPremiumNFTTokenId
        : '0',
      extraData: []
    });

    if (txHash) {
      showSuccessToast(
        `Transaction successful`,
        <ExternalLink txHash={txHash} />
      );

      onClose();
    }
  };

  return (
    <FormProvider {...methods}>
      <VStack
        as="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        alignItems="flex-start"
        gap={5}
        mt={6}
      >
        <DetailsSection
          auctionType={listing.type}
          auctionDescription={getAuctionDescription(listing.type)}
          balance={
            balance
              ? `${formatNumber(+balance, 3)} ${sellCurrency.symbol}`
              : '-'
          }
          reservePrice={
            listing.reserve && sellCurrency
              ? `${formatUnits(listing.reserve, sellCurrency?.decimals)} ${
                  sellCurrency.symbol
                }`
              : '-'
          }
          discount={discount}
          duration={[listing.start, listing.end]}
          startPrice={`${formatUnits(listing.price, sellCurrency?.decimals)} ${
            sellCurrency.symbol
          }`}
          currentPrice={
            currentPrice
              ? `${formatUnits(currentPrice, sellCurrency?.decimals)} ${
                  sellCurrency.symbol
                }`
              : '-'
          }
          deposit={data?.amount}
          depositUsd={usdAmount}
        />

        <FormControl name="amount">
          <AmountInput
            label="Price"
            handleTokenSelection={val => {
              setValue('token', val, { shouldValidate: true });
            }}
            handleValueChange={val => {
              setValue('amount', val, { shouldValidate: true });
            }}
            value={amount}
            token={token}
          />
        </FormControl>

        <SubmitSection />
      </VStack>
    </FormProvider>
  );
};
